<template>
  <v-card outlined class="fill-height">
    <v-card-title class="text-subtitle-1 py-2">
      <v-icon class="mr-2" small>{{ icon }}</v-icon>
      {{ title }}
    </v-card-title>
    <v-progress-linear v-if="loading" indeterminate/>
    <v-card-text class="pt-0">
      <div v-if="!loading && items.length === 0" class="text-body-2 text--secondary">
        {{ $t('no_data') }}
      </div>
      <v-simple-table v-else dense>
        <thead>
          <tr>
            <th class="text-left">{{ entityHeader }}</th>
            <th class="text-right">{{ $tc('request', 2) }}</th>
            <th class="text-right">{{ $t('records_returned') }}</th>
            <th class="text-right">{{ $t('avg_response_time_ms') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.key">
            <td>
              <!--
                A real link rather than a click handler, so the drill-down can be
                opened in a new tab and reads as navigation to a screen reader.
                The target is built by the parent: see drillDownTo().
              -->
              <router-link :to="item.to" :title="$t('view_matching_requests')">
                <span class="entity-label">{{ item.label }}</span>
              </router-link>
              <v-chip v-if="item.internal" x-small outlined class="ml-1">{{ $t('internal_endpoint') }}</v-chip>
            </td>
            <td class="text-right">{{ item.requests.toLocaleString() }}</td>
            <td class="text-right">{{ item.recordsReturned.toLocaleString() }}</td>
            <td class="text-right">{{ formatMilliseconds(item.avgResponseTimeMs) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>

/**
 * One "top N" table on the API usage view: the busiest callers, customers or
 * endpoints, each row linking into the request browser filtered to that entity.
 *
 * Purely presentational. Every row arrives with the router target the parent
 * built for it, which keeps the drill-down contract (both date bounds, always)
 * in a single place rather than repeated once per list. See
 * ApiAccessLogStatistics.vue.
 */
export default {
  name: 'ApiAccessLogTopList',
  props: {
    /**
     * Heading for the card.
     */
    title: {
      type: String,
      required: true
    },
    /**
     * Heading for the first column, naming what the rows are.
     */
    entityHeader: {
      type: String,
      required: true
    },
    /**
     * Icon shown next to the heading.
     */
    icon: {
      type: String,
      default: 'mdi-podium-gold'
    },
    /**
     * Rows to show, in the order they should appear. Each row needs:
     * key, label, to, requests, recordsReturned, avgResponseTimeMs and
     * optionally internal.
     */
    items: {
      type: Array,
      default: () => []
    },
    /**
     * Whether the rows are still being fetched.
     */
    loading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    /**
     * A response time, or a dash where none was measured. Null is not zero here:
     * the API leaves the average null when no request in the group had a
     * measured duration, and rendering that as 0 ms would claim they were
     * instant.
     *
     * @param {Number|null} value
     * @return String
     */
    formatMilliseconds (value) {
      return value == null ? '-' : value.toLocaleString()
    }
  }
}
</script>

<style scoped lang="scss">
// Paths are long and have no spaces to wrap on.
.entity-label {
  overflow-wrap: anywhere;
}
</style>
